import {
  Switch,
  InputBase,
  createTheme,
  Theme,
  createStyles,
} from "@material-ui/core";
import withStyles, { CSSProperties } from "@material-ui/core/styles/withStyles";
import { store } from "./store";
import { isTruthy } from "../helpers/methods";

const getCurrentMainColor = () => {
  return isTruthy(store.getState().menu.theme)
    ? store.getState().menu.theme.main
    : defaultTheme.main;
};

const getCurrentAccentColor = () => {
  return isTruthy(store.getState().menu.theme)
    ? store.getState().menu.theme.accent
    : defaultTheme.accent;
};

const getCurrentBackgroundColor = () => {
  return isTruthy(store.getState().menu.theme)
    ? store.getState().menu.theme.background
    : defaultTheme.background;
};

const getCurrentAccentTextColor = () => {
  return isTruthy(store.getState().menu.theme)
    ? store.getState().menu.theme.accentText
    : defaultTheme.accentText;
};

const getCurrentBackgroundTextColor = () => {
  return isTruthy(store.getState().menu.theme)
    ? store.getState().menu.theme.backgroundText
    : defaultTheme.backgroundText;
};

const borderRadius = "30px";

const primaryColor = "#0DD0C4";
const primaryBackgroundColor = "#EDEDED";
const borderColor = "rgba(0, 0, 0, 0.12)";
const borderStyle = "1px solid " + borderColor;
const infoTextColor = "#333333";
const defaultFontSize = 14;
const defaultBoxShadow = "0 0 0 0.2rem rgb(0 123 255 / 25%)";
const lightGrey = "#BABABA";
const darkGrey = "#888888";
const mutedText = "#666666";

//black collection
const blackFont: CSSProperties = {
  fontFamily: "TT_Norms_Pro_Black",
};
const blackItalicFont: CSSProperties = {
  fontFamily: "TT_Norms_Pro_Black_Italic",
};
export const extraBlackFont: CSSProperties = {
  fontFamily: "TT_Norms_Pro_Extra_Black",
};
const extraBlackItalicFont: CSSProperties = {
  fontFamily: "TT_Norms_Pro_Extra_Black_Italic",
};

//bold collection
const boldFont: CSSProperties = {
  fontFamily: "TT_Norms_Pro_Bold",
  fontWeight: "bold",
};
const boldItalicFont: CSSProperties = {
  fontFamily: "TT_Norms_Pro_Bold_Italic",
};
const extraBoldFont: CSSProperties = {
  fontFamily: "TT_Norms_Pro_ExtraBold",
};
const extraBoldItalicFont: CSSProperties = {
  fontFamily: "TT_Norms_Pro_Extra_Bold_Italic",
};

//light collection
const lightFont: CSSProperties = {
  fontFamily: "TT_Norms_Pro_Light",
};
const lightItalicFont: CSSProperties = {
  fontFamily: "TT_Norms_Pro_Light_Italic",
};

//medium collection
const mediumFont: CSSProperties = {
  fontFamily: "TT_Norms_Pro_Medium",
};
const mediumItalicFont: CSSProperties = {
  fontFamily: "TT_Norms_Pro_Medium_Italic",
};

//regular collection
const regularFont: CSSProperties = {
  fontFamily: "TT_Norms_Pro_Regular",
};

//thin collection
const thinFont: CSSProperties = {
  fontFamily: "TT_Norms_Pro_Thin",
};
const thinItalicFont: CSSProperties = {
  fontFamily: "TT_Norms_Pro_Thin_Italic",
};

const theme = createTheme(
  {
    typography: {
      fontFamily: [
        "-apple-system",
        "BlinkMacSystemFont",
        "TT_Norms_Pro_Regular",
        "TT_Norms_Pro_Thin",
        "TT_Norms_Pro_Bold",
        "TT_Norms_Pro_Light",
        "TT_Norms_Pro_Medium",
        "sans-serif",
      ].join(","),
    },
    // overrides: {
    //   MuiCssBaseline: {
    //     "@global": {
    //       "@font-face": [raleway],
    //     },
    //   },
    // },
  },
  { index: 1 }
);

const getRelativeFontSize = (value: number = 0) => {
  let size = defaultFontSize + value;
  return size + "px";
};

// Standardized font size classes with rem values
const standardFontSizes = {
  // Small text: 12px = 0.75rem (previously 11px)
  small: "0.75rem",
  smallPx: "12px",
  
  // Paragraph text: 16px = 1rem (previously 15px) 
  paragraph: "1rem",
  paragraphPx: "16px",
  
  // Heading text: 20px = 1.25rem (previously 19px/17px)
  heading: "1.5rem", 
  headingPx: "20px",
  
  // Additional common sizes for migration
  xs: "0.625rem", // 10px
  sm: "0.75rem",  // 12px
  base: "1rem",   // 16px 
  lg: "1.125rem", // 18px
  xl: "1.25rem",  // 20px
  "2xl": "1.5rem", // 24px
};

// Font size utility classes as CSS properties
const smallTextStyle: CSSProperties = {
  fontSize: standardFontSizes.small,
};

const paragraphTextStyle: CSSProperties = {
  fontSize: standardFontSizes.paragraph,
};

const headingTextStyle: CSSProperties = {
  fontSize: standardFontSizes.heading,
};

// Additional utility styles for common sizes
const xsTextStyle: CSSProperties = {
  fontSize: standardFontSizes.xs,
};

const smTextStyle: CSSProperties = {
  fontSize: standardFontSizes.sm,
};

const baseTextStyle: CSSProperties = {
  fontSize: standardFontSizes.base,
};

const lgTextStyle: CSSProperties = {
  fontSize: standardFontSizes.lg,
};

const xlTextStyle: CSSProperties = {
  fontSize: standardFontSizes.xl,
};

const xxlTextStyle: CSSProperties = {
  fontSize: standardFontSizes["2xl"],
};

const defaultTheme = {
  main: primaryColor,
  background: "#E0E0E0",
  backgroundText: "#000000",
  accent: primaryColor,
  accentText: "#FFFFFF",
};

const customButtonStyle: CSSProperties = {
  borderRadius: borderRadius,
  border: "none",
  fontSize: getRelativeFontSize(),
  textAlign: "center",
  padding: "12px 16px",
  boxShadow: "none",
  cursor: "pointer",
  textTransform: "none",
};

const customTextFieldStyle: CSSProperties = {
  borderRadius: borderRadius,
  position: "relative",
  border: "none",
  fontSize: standardFontSizes.paragraph,
  backgroundColor: primaryBackgroundColor,
  padding: "12px 16px",
  boxShadow: "none",
  width: "100%",
};

const headingText: CSSProperties = {
  display: "inline-block",
  fontSize: standardFontSizes.heading,
};

const centerItemFlex: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const centerItemAbsolute: CSSProperties = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%,-50%)",
};

const CustomInput = withStyles((theme: Theme) =>
  createStyles({
    root: {
      "label + &": {
        marginTop: theme.spacing(3),
      },
      backgroundColor: theme.palette.common.white,
      border: borderStyle,
      borderRadius: borderRadius,
      padding: "0 5px",
    },
    input: {
      position: "relative",
      fontSize: getRelativeFontSize(),
      width: "100%",
      padding: "10px 12px",
    },
  })
)(InputBase);

const TealSwitch = withStyles(
  {
    switchBase: {
      color: "grey",
      "&$checked": {
        color: getCurrentAccentTextColor(),
        "& + $track": {
          backgroundColor: getCurrentAccentColor(),
        },
      },
      "&$checked + $track": {
        color: getCurrentAccentTextColor(),
      },
    },
    track: { backgroundColor: "grey" },
    checked: {},
  },
  { index: 1 }
)(Switch);

export const BootstrapInput = withStyles((theme: Theme) =>
  createStyles({
    root: {
      "label + &": {
        marginTop: theme.spacing(3),
      },
      backgroundColor: theme.palette.common.white,
      border: borderStyle,
      borderRadius: borderRadius,
      padding: "0 5px",
    },
    input: {
      position: "relative",
      fontSize: getRelativeFontSize(),
      width: "100%",
      padding: "10px 8px",
    },
  })
)(InputBase);

export {
  darkGrey,
  borderRadius,
  primaryColor,
  lightGrey,
  primaryBackgroundColor,
  borderColor,
  borderStyle,
  infoTextColor,
  defaultBoxShadow,
  customButtonStyle,
  customTextFieldStyle,
  headingText,
  centerItemFlex,
  centerItemAbsolute,
  CustomInput,
  TealSwitch,
  regularFont,
  thinFont,
  lightFont,
  boldFont,
  mediumFont,
  getRelativeFontSize,
  theme,
  defaultTheme,
  getCurrentAccentColor,
  getCurrentAccentTextColor,
  getCurrentBackgroundTextColor,
  getCurrentBackgroundColor,
  getCurrentMainColor,
  mutedText,
  // New standardized font sizes
  standardFontSizes,
  smallTextStyle,
  paragraphTextStyle,
  headingTextStyle,
  xsTextStyle,
  smTextStyle,
  baseTextStyle,
  lgTextStyle,
  xlTextStyle,
  xxlTextStyle,
};
